import { useEffect, useState } from "react";
import { Accessibility, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AdaRequestForm, emptyAdaRequest, validateAdaRequest } from "@/components/ada/AdaRequestForm";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const STATUS_STYLE = {
    requested: "bg-slate-100 text-slate-700", under_review: "bg-amber-100 text-amber-800",
    sent: "bg-violet-100 text-violet-800", approved: "bg-sky-100 text-sky-800",
    implemented: "bg-emerald-100 text-emerald-800", denied: "bg-red-100 text-red-800",
};

export function InmateAdaDialog({ open, onClose }) {
    const [meta, setMeta] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(() => emptyAdaRequest());

    const load = () => {
        setLoading(true);
        api.get("/inmate/ada/accommodations").then((r) => setRows(r.data.rows || [])).catch(() => setRows([])).finally(() => setLoading(false));
    };
    useEffect(() => {
        if (!open) return;
        api.get("/inmate/ada/meta").then((r) => setMeta(r.data)).catch(() => {});
        load();
    }, [open]);

    const submit = async () => {
        const error = validateAdaRequest(form);
        if (error) return toast.error(error);
        setBusy(true);
        try {
            await api.post("/inmate/ada/accommodations", form);
            toast.success("Request submitted. The ADA Coordinator will review it.");
            setForm(emptyAdaRequest());
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit."); } finally { setBusy(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="inmate-ada-dialog" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl my-8">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-2 font-display font-bold text-phoenix-navy"><Accessibility className="w-5 h-5 text-indigo-700" /> Disability accommodation</div>
                    <button data-testid="inmate-ada-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-muted-foreground">Request a change needed for equal access because of a disability. Your request goes to the facility's ADA Coordinator.</p>
                    <AdaRequestForm meta={meta} value={form} onChange={setForm} onSubmit={submit} busy={busy}
                        testPrefix="inmate-ada" submitLabel="Submit request" />
                    <div className="pt-2 border-t border-border">
                        <div className="text-xs font-semibold text-muted-foreground mb-2">My requests</div>
                        {loading ? <div data-testid="inmate-ada-loading" className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin" /></div>
                            : !rows.length ? <div data-testid="inmate-ada-empty" className="text-sm text-muted-foreground py-2">You haven't submitted any requests yet.</div>
                                : <ul className="space-y-2">{rows.map((r) => (
                                    <li key={r.id} data-testid={`inmate-ada-item-${r.id}`} className="border border-border rounded-lg px-3 py-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium text-phoenix-navy">{r.category_label}</span>
                                            <span data-testid={`inmate-ada-status-${r.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || ""}`}>{r.status_label || (r.status || "").replace("_", " ")}</span>
                                        </div>
                                        {r.functional_need && <p className="text-xs text-muted-foreground mt-0.5"><span className="font-semibold">Barrier:</span> {r.functional_need}</p>}
                                        {r.requested_accommodation && <p className="text-xs text-muted-foreground mt-0.5"><span className="font-semibold">Requested:</span> {r.requested_accommodation}</p>}
                                        {r.accommodation_provided && <p className="text-xs text-emerald-700 mt-1"><span className="font-semibold">Provided:</span> {r.accommodation_provided}</p>}
                                        <div className="text-[10px] text-muted-foreground mt-1">Submitted {fmt(r.requested_at)}</div>
                                    </li>
                                ))}</ul>}
                    </div>
                </div>
            </div>
        </div>
    );
}